// Identifies the browser behind each API request so the backend can link the
// same person across transfers, including a sender who then claims their own
// payment in the receiving app, which sends the same headers. See
// device_activity in neuralpayments-backend.
//
// Sends, on every request to our API:
// - X-NP-App: which app sent the request
// - X-NP-Device-Id: a random id kept in a cookie shared by all our apps on
//   this domain (so the sending and receiving apps report the same id), and in
//   localStorage for when that cookie is blocked, e.g. inside an FI's iframe
// - X-NP-Device-Fingerprint: a hash of browser/hardware traits, which survives
//   clearing storage but is shared by identical devices, so it is only used
//   for analysis
// - X-NP-Timezone: the browser's IANA timezone
//
// None of this may ever break the app: every browser API used here can be
// missing or throw (private windows, blocked storage, old browsers), and each
// one falls back to sending less.

const DEVICE_ID_KEY = "np_device_id";
// Browsers cap cookie lifetimes at 400 days
const DEVICE_ID_COOKIE_MAX_AGE = 400 * 24 * 60 * 60;

let memoryDeviceId = null;
let fingerprintPromise = null;

function randomId() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    if (window.crypto && typeof window.crypto.getRandomValues === "function") {
      const bytes = window.crypto.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch (e) {
    // fall through
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// The domain our apps share, so receive.neuralpayments.com and
// send.neuralpayments.com both use neuralpayments.com, and the staging apps
// staging.neuralpayments.com. Null for localhost and IP addresses, where a
// cookie without a domain is already shared across ports.
export function cookieDomainFor(hostname) {
  if (!hostname || /^[\d.]+$/.test(hostname) || hostname.includes(":")) {
    return null;
  }
  const labels = hostname.split(".");
  if (labels.length < 2) {
    return null;
  }
  if (labels.length === 2) {
    return hostname;
  }
  return labels.slice(1).join(".");
}

function readCookie(name) {
  try {
    const prefix = `${name}=`;
    const match = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(prefix));
    return match ? decodeURIComponent(match.slice(prefix.length)) : null;
  } catch (e) {
    return null;
  }
}

function writeCookie(name, value) {
  try {
    const domain = cookieDomainFor(window.location.hostname);
    document.cookie = [
      `${name}=${encodeURIComponent(value)}`,
      "path=/",
      `max-age=${DEVICE_ID_COOKIE_MAX_AGE}`,
      "samesite=lax",
      domain ? `domain=${domain}` : null,
      window.location.protocol === "https:" ? "secure" : null,
    ]
      .filter(Boolean)
      .join("; ");
  } catch (e) {
    // blocked, e.g. third-party cookies inside an iframe
  }
}

function readStorage() {
  try {
    return window.localStorage.getItem(DEVICE_ID_KEY);
  } catch (e) {
    return null;
  }
}

function writeStorage(value) {
  try {
    window.localStorage.setItem(DEVICE_ID_KEY, value);
  } catch (e) {
    // blocked, e.g. a private window
  }
}

// The same id for this browser across visits and across our apps. The shared
// cookie wins so both apps agree; localStorage covers contexts where the
// cookie is blocked; when both are blocked it still stays the same for the
// life of the page. Whichever id is used is written back to both.
export function getDeviceId() {
  const id =
    readCookie(DEVICE_ID_KEY) || readStorage() || memoryDeviceId || randomId();
  memoryDeviceId = id;
  if (readCookie(DEVICE_ID_KEY) !== id) {
    writeCookie(DEVICE_ID_KEY, id);
  }
  if (readStorage() !== id) {
    writeStorage(id);
  }
  return id;
}

export function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (e) {
    return null;
  }
}

function safe(read) {
  try {
    const value = read();
    return value === undefined ? null : value;
  } catch (e) {
    return null;
  }
}

function webglInfo() {
  const canvas = document.createElement("canvas");
  const gl =
    canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
  if (!gl) {
    return null;
  }
  const debug = gl.getExtension("WEBGL_debug_renderer_info");
  return {
    vendor: debug
      ? gl.getParameter(debug.UNMASKED_VENDOR_WEBGL)
      : gl.getParameter(gl.VENDOR),
    renderer: debug
      ? gl.getParameter(debug.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER),
  };
}

// Text and shapes render slightly differently across GPUs, fonts and drivers
function canvasSample() {
  const canvas = document.createElement("canvas");
  canvas.width = 240;
  canvas.height = 60;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  ctx.textBaseline = "top";
  ctx.font = "16px 'Arial'";
  ctx.fillStyle = "#f60";
  ctx.fillRect(100, 5, 80, 30);
  ctx.fillStyle = "#069";
  ctx.fillText("Neural Payments \u{1F4B8}", 4, 10);
  ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
  ctx.fillText("Neural Payments \u{1F4B8}", 8, 14);
  return canvas.toDataURL();
}

// Traits that stay the same across visits and browser updates, so the
// user agent (which changes on every update) is left out, and so are screen
// size and pixel density, which change when a window moves to another monitor.
function fingerprintTraits() {
  const nav = window.navigator || {};
  return {
    platform: safe(() => nav.platform),
    languages: safe(() => (nav.languages || [nav.language]).join(",")),
    hardwareConcurrency: safe(() => nav.hardwareConcurrency),
    deviceMemory: safe(() => nav.deviceMemory),
    maxTouchPoints: safe(() => nav.maxTouchPoints),
    timezone: getTimezone(),
    webgl: safe(webglInfo),
    canvas: safe(canvasSample),
  };
}

async function sha256Hex(text) {
  const bytes = new TextEncoder().encode(text);
  const digest = await window.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");
}

// Computed once per page load. Resolves to null when the browser can't hash
// (crypto.subtle only exists on https and localhost).
export function getFingerprint() {
  if (!fingerprintPromise) {
    fingerprintPromise = (async () => {
      try {
        if (!window.crypto || !window.crypto.subtle) {
          return null;
        }
        return await sha256Hex(JSON.stringify(fingerprintTraits()));
      } catch (e) {
        return null;
      }
    })();
  }
  return fingerprintPromise;
}

export async function deviceHeaders(appName) {
  const headers = {
    "X-NP-App": appName,
    "X-NP-Device-Id": getDeviceId(),
  };
  const fingerprint = await getFingerprint();
  if (fingerprint) {
    headers["X-NP-Device-Fingerprint"] = fingerprint;
  }
  const timezone = getTimezone();
  if (timezone) {
    headers["X-NP-Timezone"] = timezone;
  }
  return headers;
}

// Adds the device headers to every request made by an axios instance
export function attachDeviceHeaders(instance, appName) {
  instance.interceptors.request.use(async (config) => {
    try {
      const headers = await deviceHeaders(appName);
      Object.entries(headers).forEach(([name, value]) => {
        config.headers[name] = value;
      });
    } catch (e) {
      // never hold up a request over tracking
    }
    return config;
  });
  return instance;
}

// For tests
export function resetDeviceTrackingForTests() {
  memoryDeviceId = null;
  fingerprintPromise = null;
}
